/*
Copyright 2023, 2024 New Vector Ltd.
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  facingModeFromLocalTrack,
  type LocalParticipant,
  LocalVideoTrack,
  Room,
  TrackEvent,
  type VideoCaptureOptions,
} from "livekit-client";
import {
  combineLatest,
  fromEvent,
  map,
  merge,
  type Observable,
  of,
  startWith,
  switchMap,
} from "rxjs";
import { logger } from "matrix-js-sdk/lib/logger";

import { type Behavior } from "../Behavior";
import {
  type BaseUserMediaInputs,
  type BaseUserMediaViewModel,
  createBaseUserMedia,
} from "./UserMediaViewModel";
import { type ObservableScope } from "../ObservableScope";
import { alwaysShowSelf } from "../../settings/settings";
import { platform } from "../../Platform";
import { type MediaDevices } from "../MediaDevices";
import {
  type CameraDevice,
  type CameraFacing,
  cameraChanged,
  cameraSwitchRequests,
  oppositeVideoDeviceId,
} from "./oppositeCamera";

export interface LocalUserMediaViewModel extends BaseUserMediaViewModel {
  local: true;
  /**
   * Whether the video should be mirrored.
   */
  mirror$: Behavior<boolean>;
  /**
   * Whether to show this tile in a highly visible location near the start of
   * the grid.
   */
  alwaysShow$: Behavior<boolean>;
  setAlwaysShow: (value: boolean) => void;
  switchCamera$: Behavior<(() => void) | null>;
}

export interface LocalUserMediaInputs extends Omit<
  BaseUserMediaInputs,
  "statsType"
> {
  participant$: Behavior<LocalParticipant | null>;
  mediaDevices: MediaDevices;
}

export function createLocalUserMedia(
  scope: ObservableScope,
  { mediaDevices, ...inputs }: LocalUserMediaInputs,
): LocalUserMediaViewModel {
  const baseUserMedia = createBaseUserMedia(scope, {
    ...inputs,
    statsType: "outbound-rtp",
  });

  /**
   * The local video track as an observable that emits whenever the track
   * changes, the camera is switched, or the track is muted.
   */
  const videoTrack$: Observable<LocalVideoTrack | null> =
    baseUserMedia.video$.pipe(
      switchMap((v) => {
        const track = v?.publication.track;
        if (!(track instanceof LocalVideoTrack)) return of(null);
        return merge(
          // Watch for track restarts because they indicate a camera switch.
          // This event is also emitted when unmuting the track object.
          fromEvent(track, TrackEvent.Restarted).pipe(
            startWith(null),
            map(() => track),
          ),
          // When the track object is muted, reset it to null.
          fromEvent(track, TrackEvent.Muted).pipe(map(() => null)),
        );
      }),
    );

  return {
    ...baseUserMedia,
    local: true,
    mirror$: scope.behavior(
      videoTrack$.pipe(
        // Mirror only front-facing cameras (those that face the user)
        map(
          (track) =>
            track !== null &&
            facingModeFromLocalTrack(track).facingMode === "user",
        ),
      ),
    ),
    alwaysShow$: alwaysShowSelf.value$,
    setAlwaysShow: alwaysShowSelf.setValue,
    switchCamera$: scope.behavior(
      platform === "desktop"
        ? of(null)
        : combineLatest([videoTrack$, mediaDevices.videoInput.available$]).pipe(
            map(([track, available]) => {
              if (track === null) return null;
              const facingMode = facingModeFromLocalTrack(track).facingMode;
              // If the camera isn't front or back-facing, don't provide a switch
              // camera shortcut at all
              if (facingMode !== "user" && facingMode !== "environment")
                return null;
              const nextFacing = facingMode === "user" ? "environment" : "user";
              const listed = [...available.entries()].map(
                ([deviceId, label]) => ({
                  deviceId,
                  label: label.type === "name" ? label.name : "",
                }),
              );
              return (): void =>
                void switchToOtherCamera(track, nextFacing, listed)
                  .then(() => {
                    // Inform the MediaDevices which camera was chosen
                    const deviceId =
                      track.mediaStreamTrack.getSettings().deviceId;
                    if (deviceId !== undefined)
                      mediaDevices.videoInput.select(deviceId);
                  })
                  .catch((e) =>
                    logger.error("Failed to switch camera", facingMode, e),
                  );
            }),
          ),
    ),
  };
}

/**
 * The list the view model holds was read before the camera was allowed and can
 * carry no names. The names are there once the camera runs, so read it again.
 */
async function camerasNow(
  listed: readonly CameraDevice[],
): Promise<readonly CameraDevice[]> {
  try {
    const devices = await Room.getLocalDevices("videoinput", false);
    const named = devices.filter(
      (device) => device.deviceId !== "" && device.label !== "",
    );
    if (named.length > 1) {
      return named.map(({ deviceId, label }) => ({ deviceId, label }));
    }
  } catch (e) {
    logger.debug("Could not list the cameras again", e);
  }
  return listed;
}

/**
 * Asks for the other camera by name first. iOS answers a request that only
 * names a side with the camera already running, so each answer is checked.
 */
async function switchToOtherCamera(
  track: LocalVideoTrack,
  nextFacing: CameraFacing,
  listed: readonly CameraDevice[],
): Promise<void> {
  const before = track.mediaStreamTrack.getSettings();
  const nextDeviceId = oppositeVideoDeviceId(
    await camerasNow(listed),
    before.deviceId,
    track.mediaStreamTrack.label,
  );
  for (const request of cameraSwitchRequests(nextFacing, nextDeviceId)) {
    try {
      // LiveKit's types only list a plain facingMode. It hands the value on to
      // getUserMedia unchanged, which is what an exact request needs.
      await track.restartTrack(request as VideoCaptureOptions);
    } catch (e) {
      logger.warn("The camera did not accept the switch request", request, e);
      continue;
    }
    if (cameraChanged(before, track.mediaStreamTrack.getSettings())) return;
  }
  logger.warn("The camera switch kept the camera that was already running");
}
